import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { resetPassword } from '../auth/api';
import { AuthCard, Button, FieldLabel, FormError, TextInput } from '../components/FormPrimitives';

/**
 * "Choose a new password" page — landed on from the email link.
 *
 * The token comes from the URL query string (?token=…). We don't validate it
 * client-side — the backend handles all the "expired/used/unknown" cases and
 * surfaces a friendly error. Once successful, we drop the user back on
 * /login so they can sign in with the new password.
 */
export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  if (!token) {
    return (
      <AuthCard title="Link incomplete" subtitle="The reset link in your email looks broken.">
        <p style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 1.5, margin: '0 0 16px' }}>
          The token piece of the URL is missing. Try opening the link from your email again, or
          request a fresh one.
        </p>
        <Link
          to="/forgot-password"
          style={{ color: 'var(--brand-primary)', fontWeight: 500, fontSize: 14, textDecoration: 'none' }}
        >
          Request a new reset email →
        </Link>
      </AuthCard>
    );
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (!/[A-Z]/.test(password)) {
      setError('Password must contain at least one uppercase letter.');
      return;
    }
    if (!/[0-9]/.test(password)) {
      setError('Password must contain at least one number.');
      return;
    }

    setSubmitting(true);
    try {
      await resetPassword(token!, password);
      setDone(true);
      // Auto-redirect to login after 2 seconds so the user sees the success
      // state but doesn't have to click anything.
      setTimeout(() => navigate('/login', { replace: true }), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Try again.');
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <AuthCard title="Password updated" subtitle="You can now sign in with your new password.">
        <p style={{ fontSize: 14, color: 'var(--text-muted)', margin: '0 0 16px' }}>
          Redirecting to sign-in…
        </p>
        <Link
          to="/login"
          style={{ color: 'var(--brand-primary)', fontWeight: 500, fontSize: 14, textDecoration: 'none' }}
        >
          Sign in now →
        </Link>
      </AuthCard>
    );
  }

  return (
    <AuthCard title="Choose a new password" subtitle="Minimum 8 characters, with at least 1 uppercase letter and 1 number.">
      <form onSubmit={onSubmit} noValidate>
        <FormError message={error} />

        <div style={{ marginBottom: 16 }}>
          <FieldLabel htmlFor="password">New password</FieldLabel>
          <TextInput
            id="password"
            name="password"
            type="password"
            autoComplete="new-password"
            autoFocus
            required
            value={password}
            onChange={(e) => setPassword(e.currentTarget.value)}
            disabled={submitting}
          />
        </div>

        <div style={{ marginBottom: 20 }}>
          <FieldLabel htmlFor="confirm">Confirm new password</FieldLabel>
          <TextInput
            id="confirm"
            name="confirm"
            type="password"
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.currentTarget.value)}
            disabled={submitting}
          />
        </div>

        <Button
          type="submit"
          disabled={submitting || !password || !confirm}
          style={{ width: '100%', marginBottom: 12 }}
        >
          {submitting ? 'Updating…' : 'Set new password'}
        </Button>

        <div style={{ textAlign: 'center' }}>
          <Link
            to="/login"
            style={{ textDecoration: 'none', color: 'var(--brand-primary)', fontSize: 13, fontWeight: 500 }}
          >
            ← Back to sign in
          </Link>
        </div>
      </form>
    </AuthCard>
  );
}
